import TextLink from 'LuxKit/TextLink'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'

interface ApiError {
  status: number
  message: string
  errorCode?: string
}

function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && 'status' in err
}

export function getTierUpgradeClaimErrorDetails(err: unknown): { status?: number, errorCode?: string } {
  if (!isApiError(err)) return {}
  return { status: err.status, errorCode: err.errorCode }
}

/** Returns a function that maps a tier upgrade claim API error to customer-facing copy. */
function useTierUpgradeClaimErrorMessage(campaignId: string): (err: unknown) => React.ReactNode {
  const intl = useIntl()
  const claimCampaign = useAppSelector(state => state.loyalty.claimCampaigns.data?.[campaignId])

  const allowedDomainsText = intl.formatList((claimCampaign?.emailVerificationDomains ?? []).map(domain => `@${domain}`), {
    type: 'disjunction',
    style: 'short',
  })

  const emailDomainNotAllowedMessage = allowedDomainsText
    ? intl.formatMessage({
        id: 'u--jQdF1vycLRJsiLB3a',
        defaultMessage: 'This offer is only available to customers with an eligible email address. To access it, your Luxury Escapes account must be registered with one of the following domains: {allowedDomains}.',
        description: 'Message listing the allowed account email domains after a tier upgrade campaign rejects an account email',
      }, { allowedDomains: allowedDomainsText })
    : intl.formatMessage({
        id: 'byFwLNEv2cfyE1av4otP',
        defaultMessage: 'This offer is only available to customers with an eligible email address.',
        description: 'Message shown after a tier upgrade campaign rejects an account email when allowed domains are unavailable',
      })

  const emailVerificationMessage = useMemo(
    () => <FormattedMessage
      id="tkqvNDjlmC79p6ZLnU6D"
      defaultMessage="To redeem this code, <link>verify your email address</link>."
      description="Message linking a tier upgrade campaign customer to verify their account email"
      values={{
        link: (chunks: React.ReactNode) => <TextLink to="/account" size="medium" weight="regular">{chunks}</TextLink>,
      }}
    />,
    [],
  )

  return useCallback((err: unknown) => {
    const { errorCode, status } = getTierUpgradeClaimErrorDetails(err)
    if (errorCode === 'ACCOUNT_NOT_FOUND') {
      return intl.formatMessage({
        id: 'v-mECGmgZjOoD5bGlPuG',
        defaultMessage: 'You need to join Société to redeem this code.',
        description: 'Error message when user has no loyalty account on tier upgrade campaign',
      })
    } else if (errorCode === 'CLAIMS_EMAIL_NOT_VERIFIED') {
      return emailVerificationMessage
    } else if (errorCode === 'CLAIMS_EMAIL_ELIGIBILITY_UNAVAILABLE') {
      return intl.formatMessage({
        id: 'BMnx6VWDBaqZ44suUc1b',
        defaultMessage: 'We couldn’t check whether your email address is eligible. Please try again later.',
        description: 'Error shown when tier upgrade campaign email eligibility could not be loaded',
      })
    } else if (errorCode === 'CLAIMS_EMAIL_DOMAIN_NOT_ALLOWED') {
      return emailDomainNotAllowedMessage
    } else if (errorCode === 'TIER_ALREADY_HIGHER') {
      return intl.formatMessage({
        id: 'pjMLgAaMOLx72kCSTTMr',
        defaultMessage: 'You cannot redeem a tier lower than your current tier.',
        description: 'Error message when user already has a higher tier on tier upgrade campaign',
      })
    } else if (status === 409) {
      return intl.formatMessage({
        id: 'l5l-UdFD2FSfNwR35qI9',
        defaultMessage: 'You have already redeemed this benefit.',
        description: 'Error message for duplicate claim on tier upgrade campaign',
      })
    } else if (status === 400) {
      return intl.formatMessage({
        id: 'uBhwII10LK8AZZCioWaF',
        defaultMessage: 'Invalid or expired code. Please try again.',
        description: 'Error message for invalid promo code on tier upgrade campaign',
      })
    } else if (status === 404) {
      return intl.formatMessage({
        id: '6VrJ4UpVrVYZevt_Sscp',
        defaultMessage: 'This campaign is no longer available.',
        description: 'Error message for campaign not found on tier upgrade campaign',
      })
    }
    return intl.formatMessage({
      id: 'HL73ONR5Ct2b1DJ3LPvx',
      defaultMessage: 'Something went wrong. Please try again.',
      description: 'Fallback error message for tier upgrade campaign promo code redemption',
    })
  }, [emailDomainNotAllowedMessage, emailVerificationMessage, intl])
}

export default useTierUpgradeClaimErrorMessage
